import { candidateExperienceService } from '../services/candidate-experience.service';
import { useCandidateMutation, type CandidateMutationParams } from './useCandidateMutation';
import type {
  CreateExperienceRequest,
  UpdateExperienceRequest,
} from '@/features/experiences/types/experience.types';

export interface CandidateExperienceMutations {
  createExperience: (request: CreateExperienceRequest) => void;
  updateExperience: (id: string, request: UpdateExperienceRequest) => void;
  deleteExperience: (id: string) => void;
}

export function useCandidateExperienceMutations({
  queryKey,
  isAnonymous,
  shareId,
}: CandidateMutationParams): CandidateExperienceMutations {
  const createExperience = useCandidateMutation(
    queryKey,
    (request: CreateExperienceRequest) =>
      isAnonymous
        ? candidateExperienceService.createByShareId(shareId!, request)
        : candidateExperienceService.create(request),
    { success: 'Experience added', error: 'Failed to add experience' }
  );
  const update = useCandidateMutation(
    queryKey,
    ({ id, request }: { id: string; request: UpdateExperienceRequest }) =>
      isAnonymous
        ? candidateExperienceService.updateByShareId(shareId!, id, request)
        : candidateExperienceService.update(id, request),
    { success: 'Experience updated', error: 'Failed to update experience' }
  );
  const deleteExperience = useCandidateMutation(
    queryKey,
    (id: string) =>
      isAnonymous
        ? candidateExperienceService.deleteByShareId(shareId!, id)
        : candidateExperienceService.delete(id),
    { success: 'Experience deleted', error: 'Failed to delete experience' }
  );

  return {
    createExperience,
    updateExperience: (id, request) => update({ id, request }),
    deleteExperience,
  };
}
